import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { toast } from 'sonner';
import type { TumUnit } from '@/types/tum';

export const useTumUnits = () => {
  const workspace = useWorkspace();
  const currentWorkspace = workspace?.currentWorkspace;
  const queryClient = useQueryClient();

  const { data: units, isLoading, error } = useQuery({
    queryKey: ['tum-units', currentWorkspace?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tum_units')
        .select(`
          *,
          property:tum_properties(*),
          site:tum_sites(*)
        `)
        .eq('workspace_id', currentWorkspace!.id)
        .order('unit_number', { ascending: true });
      
      if (error) throw error;
      return data as TumUnit[];
    },
    enabled: !!currentWorkspace?.id,
  });

  const createMutation = useMutation({
    mutationFn: async (unit: any) => {
      const { data, error } = await supabase
        .from('tum_units')
        .insert({ ...unit, workspace_id: currentWorkspace!.id })
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tum-units'] });
      toast.success('Unit created successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to create unit: ${error.message}`);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<TumUnit> & { id: string }) => {
      const { data, error } = await supabase
        .from('tum_units')
        .update(updates)
        .eq('id', id)
        .eq('workspace_id', currentWorkspace!.id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tum-units'] });
      toast.success('Unit updated successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to update unit: ${error.message}`);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('tum_units')
        .delete()
        .eq('id', id)
        .eq('workspace_id', currentWorkspace!.id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tum-units'] });
      toast.success('Unit deleted successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete unit: ${error.message}`);
    },
  });

  return {
    units: units || [],
    isLoading,
    error,
    createUnit: createMutation.mutate,
    updateUnit: updateMutation.mutate,
    deleteUnit: deleteMutation.mutate,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
};
